/* Practice carries the learning: the number of activities a student *does*
   predicts exam performance ~6x more strongly than pages read (Koedinger 2015).
   So it is not a grey box at the bottom of a reading page — and every item in
   it takes an answer. */
.practice {
  margin: 44px 0 0;
  border: 1px solid var(--rule);
  border-radius: var(--radius-xl);
  overflow: hidden;
  background: var(--paper);
}

.head { padding: 22px 26px; background: var(--panel); border-bottom: 1px solid var(--rule); }
.title { margin: 8px 0 0; font: 300 26px/1.1 var(--serif); color: var(--ink); }
.sub { margin: 8px 0 0; font: 400 13px/1.6 var(--sans); color: var(--ink-68); max-width: 58ch; }

.steps { padding: 4px 26px 26px; }
.step { border-top: 1px solid var(--rule); padding: 22px 0; }
.step:first-child { border-top: none; }

.stepHead { display: flex; align-items: baseline; gap: 12px; flex-wrap: wrap; }
.kind {
  font: 600 10px var(--mono); letter-spacing: .14em; text-transform: uppercase;
  padding: 3px 8px; border-radius: 4px; background: var(--ink-08); color: var(--ink);
}
.stepTitle { font: 500 14px var(--sans); color: var(--ink); }
.prompt { margin: 12px 0 0; font: 400 15px/1.7 var(--sans); color: var(--body); max-width: 58rem; }

/* collapsed middle step — available, not hidden */
.collapsed { opacity: .72; }
.expand {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  border: none; background: none; padding: 0; cursor: pointer; text-align: left;
}
.collapsedNote { font: 400 13px var(--sans); color: var(--ink-55); }
.expand:hover .collapsedNote { color: var(--ink); }

/* items — each one takes an answer */
.items { margin: 16px 0 0; display: flex; flex-direction: column; gap: 14px; }
.item {
  /* The code takes its own width, up to 60% of the row: a line of code that
     wraps mid-call reads as two lines to a beginner (found 2026-10-08). */
  display: grid; grid-template-columns: fit-content(60%) 1fr; gap: 14px 18px;
  align-items: center; padding: 12px 14px;
  border: 1px solid var(--ink-12); border-radius: var(--radius-lg);
  background: var(--card);
  transition: border-color .15s ease, background-color .15s ease;
}
.item.right { border-color: var(--accent); background: var(--check-bg); }
.item.wrong { border-color: var(--orange); background: var(--wrong-bg); }

.itemCode {
  font: 500 14px var(--mono); color: var(--ink);
  background: var(--ink-08); padding: 6px 10px; border-radius: var(--radius-sm);
  white-space: pre; overflow-x: auto; max-width: 100%;
}
.itemAnswer { display: flex; gap: 10px; align-items: center; flex-wrap: wrap; }
.input {
  flex: 1 1 11rem; min-width: 0; padding: 9px 12px;
  border: 1px solid var(--ink-16); border-radius: var(--radius);
  background: var(--paper); font: 500 14px var(--mono); color: var(--ink);
}
.input:disabled { opacity: .8; }
.verdict { font: 600 10.5px var(--mono); letter-spacing: .14em; text-transform: uppercase; }
.vRight { color: var(--olive); }
.vWrong { color: var(--orange); }
.why { grid-column: 1 / -1; margin: 0; font: 400 13px/1.6 var(--sans); color: var(--ink-68); }

/* served only on a miss */
.extra { margin-top: 18px; padding-left: 14px; border-left: 2px solid var(--gold-rule); }
.extraHead {
  font: 600 10.5px var(--mono); letter-spacing: .14em;
  text-transform: uppercase; color: var(--olive);
}

.exercise { margin-top: 16px; }

.hints { margin-top: 16px; }
.hintToggle {
  border: none; background: none; padding: 0; cursor: pointer;
  font: 500 10.5px var(--mono); letter-spacing: .14em; text-transform: uppercase; color: var(--olive);
}
.hintToggle:hover { color: var(--ink); }
.hint { margin: 10px 0 0; padding-left: 14px; border-left: 1px solid var(--gold-rule); }
.hintWhen { font: 600 11.5px var(--sans); color: var(--ink); }
.hintSay { margin: 3px 0 0; font: 400 13px/1.6 var(--sans); color: var(--ink-68); }

@media (max-width: 760px) {
  .head { padding: 18px 20px; }
  .steps { padding: 4px 20px 22px; }
  .item { grid-template-columns: 1fr; }
}

/* run — the program, the gate, and what it printed */
.run { margin-top: 16px; }
.runBar { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-top: 12px; }
.runNote { margin: 0; font: 400 13px var(--sans); color: var(--ink-55); }
.output {
  margin: 12px 0 0; padding: 12px 14px; border-radius: var(--radius);
  background: var(--ink-08); font: 500 13.5px/1.6 var(--mono); color: var(--ink);
  white-space: pre-wrap; word-break: break-word;
}
.outputErr { color: var(--orange); }

/* critique — numbered, because answers name lines */
.numbered { margin-top: 16px; }

/* assemble — pick lines into order */
.assemble { margin-top: 16px; padding: 14px; border: 1px solid var(--ink-12); border-radius: var(--radius-lg); background: var(--card); }
.assemble.right { border-color: var(--accent); background: var(--check-bg); }
.assemble.wrong { border-color: var(--orange); background: var(--wrong-bg); }
.assembleCols { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
.assembleHead { font: 600 10.5px var(--mono); letter-spacing: .14em; text-transform: uppercase; color: var(--olive); margin-bottom: 8px; }
.lineList { display: flex; flex-direction: column; gap: 6px; min-height: 2.5rem; }
.line {
  display: flex; gap: 10px; align-items: baseline; text-align: left; cursor: pointer;
  padding: 7px 10px; border: 1px solid var(--ink-16); border-radius: var(--radius-sm);
  background: var(--paper); font: 500 13.5px var(--mono); color: var(--ink); white-space: pre;
  overflow-x: auto;
}
.line:hover:not(:disabled) { border-color: var(--ink); }
.line:disabled { cursor: default; }
.placed { background: var(--ink-08); }
.lineWrong { border-color: var(--orange); }
.lineNo { font: 400 11px var(--mono); color: var(--ink-55); min-width: 1.4em; }

@media (max-width: 760px) {
  .assembleCols { grid-template-columns: 1fr; }
}

/* "I don't know" — available, never louder than Check (D-040). */
.idk {
  border: none; background: none; padding: 8px 4px; cursor: pointer;
  font: 500 12px var(--sans); color: var(--ink-55); text-decoration: underline;
}
.idk:hover { color: var(--ink); }

/* Choices (D-041): a closed question is buttons, not a text box. */
.choices { display: flex; flex-wrap: wrap; gap: 8px; }
.choice {
  border: 1px solid var(--ink-30); background: var(--card); color: var(--ink);
  border-radius: var(--radius); padding: 8px 14px; cursor: pointer;
  font: 500 13.5px var(--mono);
}
.choice:hover:not(:disabled) { border-color: var(--ink); }
.choice:disabled { cursor: default; opacity: .55; }
.choice.picked { opacity: 1; border-color: var(--ink); border-width: 2px; }
/* A prose question (D-041) takes the whole row; the answer sits under it. */
.prose { grid-template-columns: 1fr; }
.question { margin: 0; font: 400 15px/1.55 var(--sans); color: var(--ink); }
.prose .choice { font: 500 14px var(--sans); }   /* prose options read as prose */
